/**
 * 課題管理の画面からコピーする文（起案用の文・声の主への返信文の下書き）。
 *
 * 起案は pj-management で人が行う（D5）ので、起案に要る中身（タイトル・中身3項目・声の件数・課題の URL）を1つの文にして渡す。
 * 返信文の下書きは、判定と理由、または直った状態から作る（AI の下書きは別の PR で足す）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題から、起案用の文（タイトル・中身3項目・声の件数・課題の URL）と、声の主に結果を返す返信文の下書きを作る
 * @graph-connects none
 */
import type { IssueDetail } from '@cortex/domain-ac-mens-hub-api';

import { ISSUE_REJECT_REASON_LABEL_KEYS } from './issue-mgmt-format.js';
import type { Translate } from './issue-mgmt-values.js';

/**
 * 課題の詳細の URL。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の詳細を開く URL を作る
 * @graph-connects none
 */
export function issueUrl(issueId: string, origin: string): string {
  return `${origin}/issue-mgmt/issues/${issueId}`;
}

/**
 * 起案用の文（タイトル・中身3項目・声の件数・課題の URL）。空の項目は「まだ書かれていません」。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 起案のために、課題のタイトル・中身3項目・声の件数・課題の URL を1つの文にする
 * @graph-connects issueUrl [calls] 課題の URL
 */
export function kianText(issue: IssueDetail, origin: string, t: Translate): string {
  const empty = t('issueMgmt.content.empty');
  return [
    `${issue.title}（${issue.id}）`,
    `${t('issueMgmt.content.what')}: ${issue.content.what.text ?? empty}`,
    `${t('issueMgmt.content.guidance')}: ${issue.content.guidance.text ?? empty}`,
    `${t('issueMgmt.content.fixed')}: ${issue.content.fixed.text ?? empty}`,
    t('issueMgmt.voices.heading', { n: String(issue.voiceCount) }),
    issueUrl(issue.id, origin),
  ].join('\n');
}

/**
 * 声の主に結果を返す返信文の下書き。対応しないなら判定と理由、解決済みなら直った状態（無ければ対応が済んだことだけ）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 対応しない・解決済みの課題について、声を上げた人へ返す返信文の下書きを、理由か直った状態から作る
 * @graph-connects none
 */
export function replyDraft(issue: IssueDetail, t: Translate): string {
  if (issue.verdict === 'rejected') {
    return issue.rejectReason === null
      ? t('issueMgmt.reply.draftRejectedNoReason', { title: issue.title })
      : t('issueMgmt.reply.draftRejected', {
          title: issue.title,
          reason: t(ISSUE_REJECT_REASON_LABEL_KEYS[issue.rejectReason]),
        });
  }
  return issue.content.fixed.text === null
    ? t('issueMgmt.reply.draftResolved', { title: issue.title })
    : t('issueMgmt.reply.draftResolvedFixed', {
        title: issue.title,
        fixed: issue.content.fixed.text,
      });
}
